import React, { useContext, useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { WelletContext } from '../../context/WelletContext';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setCanRefresh } from '../../actions/appActions';
import Header from '../../components/Common/Header/Header';
import ContentApp from '../../components/ContentApp';
import Button from '../../components/Common/Button/Button';
import useForm from '../../helpers/useForm';
import ErrorBox from '../../components/Common/ErrorBox/ErrorBox';
import { Spinner } from '../../components/Common/Spinner/Spinner';
import InputFormBorder from '../../components/Common/InputFormBorder/InputFormBorder';
import FixedButton from '../../components/Common/FixedButton/FixedButton';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { patchNewEmail } from '../../services/newEmail/EmailService';
import { exchangeToken } from '../../actions/associatesActions';

const EmailRevalidationProfile = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const welletContext = useContext(WelletContext);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.associates.user);
  const userEmail = user?.email || '';

  const [verifyingUser, setVerifyingUser] = useState(false);
  const [errorUser, setErrorUser] = useState(undefined);

  useEffect(() => {
    dispatch(setCanRefresh(false));
  }, []);

  function validate(values) {
    const errors = {};

    if (!values.currentEmail) {
      errors.currentEmail = t('emailError.EMPTY_EMAIL');
    } else if (values.currentEmail !== userEmail) {
      errors.currentEmail = t('emailError.WRONG_CURRENT_EMAIL');
    }

    if (!values.newEmail) {
      errors.newEmail = t('emailError.EMPTY_EMAIL');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.newEmail)) {
      errors.newEmail = t('emailError.INVALID_EMAIL');
    } else if (values.newEmail === userEmail) {
      errors.newEmail = t('emailError.SAME_AS_OLD');
    }

    if (!values.confirmEmail) {
      errors.confirmEmail = t('emailError.EMPTY_EMAIL');
    } else if (values.newEmail !== values.confirmEmail) {
      errors.confirmEmail = t('emailError.EMAIL_NOT_MATCH');
    }

    return errors;
  }

  const sendForm = async () => {
    setVerifyingUser(true);
    setErrorUser('');

    try {
      const response = await patchNewEmail(welletContext, values.newEmail);

      if (response?.data?.errorCode === 'EMAIL_ALREADY_EXISTS') {
        setErrorUser(t('emailError.EMAIL_ALREADY_EXISTS'));
        return;
      }

      const customToken = response?.data;
      if (!customToken) throw new Error('NO_CUSTOM_TOKEN');

      const auth = getAuth();
      const userCredentials = await signInWithCustomToken(auth, customToken);

      await exchangeToken(dispatch, userCredentials.user, null, history, customToken);

      history.push('/success-email-change');
    } catch (error) {
      console.error(error);
      if (error.response?.data?.errorCode === 'EMAIL_ALREADY_EXISTS') {
        setErrorUser(t('emailError.EMAIL_ALREADY_EXISTS'));
      } else if (error.message === 'NO_CUSTOM_TOKEN') {
        setErrorUser(t('errorSignUp.GENERIC_ERROR'));
      } else {
        setErrorUser(t('errorSignUp.GENERIC_ERROR'));
      }
    } finally {
      setVerifyingUser(false);
    }
  };

  const onChange = (e) => {
    setErrorUser('');
    handleChange(e);
  };

  const { values, errors, handleChange, handleSubmit } = useForm(sendForm, validate);

  return (
    <>
      <Header />
      <ContentApp background={true}>
        <div>
          <div className='bold text-white'>{t('updateEmail')}</div>
          <div className='mt-1 text-secondary semi-bold'>{t('subtitleUpdateEmail')}</div>

          <form onSubmit={handleSubmit}>
            <div className='mt-4'>
              <InputFormBorder
                name='currentEmail'
                type='email'
                value={values.currentEmail || ''}
                error={errors.currentEmail}
                label={t('currentEmail')}
                placeholder={t('enterCurrentEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            <div className='mt-4'>
              <InputFormBorder
                name='newEmail'
                type='email'
                value={values.newEmail || ''}
                error={errors.newEmail}
                label={t('newEmail')}
                placeholder={t('enterNewEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            <div className='mt-4'>
              <InputFormBorder
                name='confirmEmail'
                type='email'
                value={values.confirmEmail || ''}
                error={errors.confirmEmail}
                label={t('confirmNewEmail')}
                placeholder={t('reEnterEmail')}
                onChange={onChange}
                forceLowecase
              />
            </div>

            {errorUser && (
              <div className='mt-3'>
                <ErrorBox title={errorUser} />
              </div>
            )}

            <div className='mt-4'>
              <FixedButton>
                <Button
                  size='large'
                  fontSize='18px'
                  text={
                    verifyingUser ? (
                      <div className='d-flex justify-content-center'>
                        <Spinner size={25} color='#000' />
                      </div>
                    ) : (
                      t('continue')
                    )
                  }
                  icon={!verifyingUser}
                  typeIcon='arrow'
                  iconColor='#000'
                />
              </FixedButton>
            </div>
          </form>
        </div>
      </ContentApp>
    </>
  );
};

export default EmailRevalidationProfile;
